/**
 * Zones Hooks (Phase 9.5)
 *
 * TanStack Query hooks for zone CRUD operations.
 * Zones represent tracked areas within stations for activity counting.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '../lib/hooks/useApi';
import type {
  ZoneWithStation,
  ZoneLookup,
  Zone,
  CreateZone,
  UpdateZone,
} from '@guild/shared';

// Query keys
export const zoneKeys = {
  all: ['zones'] as const,
  lists: () => [...zoneKeys.all, 'list'] as const,
  list: (filters: { search?: string; stationId?: string; includeInactive?: boolean }) =>
    [...zoneKeys.lists(), filters] as const,
  lookup: (stationId?: string) => [...zoneKeys.all, 'lookup', stationId] as const,
  details: () => [...zoneKeys.all, 'detail'] as const,
  detail: (id: string) => [...zoneKeys.details(), id] as const,
};

/**
 * Fetch zones list with station name
 */
export function useZones(options?: {
  search?: string;
  stationId?: string;
  includeInactive?: boolean;
}) {
  const api = useApi();

  return useQuery({
    queryKey: zoneKeys.list(options ?? {}),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (options?.search) params.set('search', options.search);
      if (options?.stationId) params.set('stationId', options.stationId);
      if (options?.includeInactive) params.set('includeInactive', 'true');

      const queryString = params.toString();
      const endpoint = queryString ? `/api/zones?${queryString}` : '/api/zones';

      return api.get<ZoneWithStation[]>(endpoint);
    },
  });
}

/**
 * Fetch lightweight zones list for dropdowns
 */
export function useZonesLookup(stationId?: string) {
  const api = useApi();

  return useQuery({
    queryKey: zoneKeys.lookup(stationId),
    queryFn: async () => {
      const params = new URLSearchParams();
      if (stationId) params.set('stationId', stationId);

      const queryString = params.toString();
      const endpoint = queryString ? `/api/zones/lookup?${queryString}` : '/api/zones/lookup';

      return api.get<ZoneLookup[]>(endpoint);
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (rarely changes)
  });
}

/**
 * Fetch single zone
 */
export function useZone(id: string | null) {
  const api = useApi();

  return useQuery({
    queryKey: zoneKeys.detail(id ?? ''),
    queryFn: () => api.get<Zone>(`/api/zones/${id}`),
    enabled: !!id,
  });
}

/**
 * Create zone mutation
 */
export function useCreateZone() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateZone) => api.post<Zone>('/api/zones', data),
    onSuccess: () => {
      // Invalidate all zone queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.all });
    },
  });
}

/**
 * Update zone mutation
 */
export function useUpdateZone() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateZone }) =>
      api.put<Zone>(`/api/zones/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate list and specific detail
      queryClient.invalidateQueries({ queryKey: zoneKeys.lists() });
      queryClient.invalidateQueries({ queryKey: zoneKeys.detail(variables.id) });
      queryClient.invalidateQueries({ queryKey: zoneKeys.lookup() });
    },
  });
}

/**
 * Delete zone mutation (soft delete)
 */
export function useDeleteZone() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => api.delete<{ id: string }>(`/api/zones/${id}`),
    onSuccess: () => {
      // Invalidate all zone queries
      queryClient.invalidateQueries({ queryKey: zoneKeys.all });
    },
  });
}
